/**
 * Centralized Portal Configuration
 * 
 * This file contains the centralized configuration for all portals, including:
 * - Portal definitions (id, name, path, icon)
 * - Role-based access control for each portal
 * - Portal metadata
 * 
 * IMPORTANT: This is the single source of truth for portal configurations.
 * All portal switcher components and protected routes should use this configuration.
 */

import Person from '@mui/icons-material/Person'
import Home from '@mui/icons-material/Home'
import Business from '@mui/icons-material/Business'
import LocalHospital from '@mui/icons-material/LocalHospital'
import MonitorHeart from '@mui/icons-material/MonitorHeart'
import TvIcon from '@mui/icons-material/Tv'
import { APP_CONSTANTS } from 'src/consumer/constants'

/**
 * Portal configuration array
 * Each portal defines:
 * - id: Unique identifier for the portal
 * - name: Display name shown in portal switcher
 * - path: Route path for the portal
 * - icon: Material-UI icon component
 * - roles: Array of role strings that grant access to this portal
 * - external: Whether the portal is an external link (opens in new tab)
 */
export const PORTAL_CONFIG = [
  {
    id: 'support',
    name: 'Support Portal',
    path: '/support-home',
    icon: Person,
    roles: [
      'ROLE_ADMIN',
      'ROLE_SUB_ADMIN',
      'ROLE_APP_MANAGER',
      'ROLE_DEVELOPER',
      'ROLE_MANAGEMENT',
      'ROLE_THERAPY_SUP_L1',
      'ROLE_THERAPY_SUP_L2',
      'ROLE_THERAPY_SUP_L3',
      'ROLE_THERAPIST_ADMIN',
      'ROLE_CLINIC_SUCCESS_MANAGER',
      'ROLE_CLINIC_SUCCESS_ADMIN',
      'ROLE_CLINIC_ROSTER_MANAGER',
      'ROLE_CLINIC_ROSTER_ADMIN',
      'ROLE_VC_AUDIT_ADMIN',
      'ROLE_VC_AUDIT_MANAGER',
      'ROLE_DOCSIDE_ADMIN',
      'ROLE_DOCSIDE_MANAGER',
      'ROLE_SUPPORT',
      'ROLE_SUPPORT_USER',
      'ROLE_PAYMENT_SUPPORT',
      'ROLE_READ_ONLY_RESTRICTED',
    ],
    external: false,
  },
  {
    id: 'mentor',
    name: 'Clinic Practitioner App',
    path: '/mentor-home',
    icon: Person,
    roles: ['ROLE_DOCTOR', 'ROLE_TEACHER', 'ROLE_THERAPIST', 'ROLE_SCREENER', 'ROLE_CREATOR'],
    external: false,
  },
  {
    id: 'clinic',
    name: 'Clinic Owners App',
    path: '/clinic-home',
    icon: LocalHospital,
    roles: [
      'ROLE_CLINIC_OWNER',
      'ROLE_CLINIC_MANAGER',
      'ROLE_CLINIC_ADMIN',
      'ROLE_CLINIC_PROSPECT',
      'ROLE_PHARMACIST',
    ],
    external: false,
  },
  {
    id: 'clinic-assistant',
    name: 'Clinic Assistant Portal',
    path: '/ca-home',
    icon: LocalHospital,
    roles: ['ROLE_CLINIC_ASSISTANT'],
    external: false,
  },
  {
    id: 'kiosk',
    name: 'KIOSK (AUMHUM Screen)',
    path: '/kiosk',
    icon: TvIcon,
    roles: ['ROLE_KIOSK', 'ROLE_AUMHUM_SCREEN'],
    external: false,
  },
  {
    id: 'display',
    name: 'Display (OOH / Token)',
    path: '/display',
    icon: TvIcon,
    roles: ['ROLE_OOH'],
    external: false,
  },
  {
    id: 'business',
    name: 'Business Portal',
    path: '/workplace',
    icon: Business,
    roles: ['ROLE_BUSINESS_CUSTOMER', 'ROLE_BUSINESS_PROSPECT'],
    external: false,
  },
  {
    // The patient portal at /patient-home is the dedicated care-management
    // surface for ROLE_PATIENT users. Distinct from the Wellness App
    // (consumer meditation / journaling / habits) which is gated on
    // ROLE_USER only — ROLE_PATIENT no longer grants access to /. A user
    // who needs both apps must hold both roles.
    id: 'patient',
    name: 'Patient App',
    path: '/patient-home',
    icon: MonitorHeart,
    roles: ['ROLE_PATIENT'],
    external: false,
  },
  {
    id: 'patient-prospect',
    name: 'Care Request',
    path: '/care/status',
    icon: MonitorHeart,
    roles: ['ROLE_PATIENT_PROSPECT'],
    external: false,
  },
  {
    id: 'consumer',
    name: 'Wellness App',
    path: '/',
    icon: Home,
    roles: ['ROLE_USER', 'ROLE_PROFESSIONAL_USER'],
    external: false,
  },
  {
    id: 'control-center',
    name: 'Control Center',
    path: APP_CONSTANTS.CONTROL_CENTER_LINK,
    icon: Business,
    roles: ['ROLE_CLINIC_OWNER'],
    external: true,
  },
]

/**
 * Get all roles that have access to a specific portal
 * @param {string} portalId - The portal ID
 * @returns {string[]} Array of role strings
 */
export function getRolesForPortal(portalId) {
  const portal = PORTAL_CONFIG.find((p) => p.id === portalId)
  return portal ? portal.roles : []
}

/**
 * Get portal configuration by ID
 * @param {string} portalId - The portal ID
 * @returns {Object|null} Portal configuration object or null
 */
export function getPortalById(portalId) {
  return PORTAL_CONFIG.find((p) => p.id === portalId) || null
}

/**
 * Get portal configuration by path
 * @param {string} path - The portal path
 * @returns {Object|null} Portal configuration object or null
 */
export function getPortalByPath(path) {
  return PORTAL_CONFIG.find((p) => p.path === path) || null
}

/**
 * Check if a user has access to a portal based on their roles
 * @param {string[]} userRoles - Array of user role strings
 * @param {string} portalId - The portal ID
 * @returns {boolean} True if user has access, false otherwise
 */
export function hasPortalAccess(userRoles, portalId) {
  const portalRoles = getRolesForPortal(portalId)
  return portalRoles.some((role) => userRoles?.includes(role))
}

/**
 * Get all portals a user has access to
 * @param {string[]} userRoles - Array of user role strings
 * @returns {Object[]} Array of portal configuration objects
 */
export function getAvailablePortals(userRoles) {
  return PORTAL_CONFIG.filter((portal) => hasPortalAccess(userRoles, portal.id))
}

/**
 * Support Portal specific roles
 * These are all the roles that grant access to the Support Portal
 */
export const SUPPORT_PORTAL_ROLES = [
  'ROLE_THERAPY_SUP_L1',
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_THERAPIST_ADMIN',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_CLINIC_ROSTER_MANAGER',
  'ROLE_CLINIC_ROSTER_ADMIN',
  'ROLE_SUPPORT',
  'ROLE_SUPPORT_USER',
  'ROLE_PAYMENT_SUPPORT',
  'ROLE_MANAGEMENT',
]
